import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, getStatus, getMe, logout, logoutAll, onUnauthorized, type AuthStatus, type Me } from './authApi';
import { nav } from './nav';
import { lockDiagnoses, setDiagnosesOwner, setSharedDiagnoses } from '../components/SimpleWISCV/utils/diagnosesStore';
import LoginScreen, { type LoginNotice } from './LoginScreen';
import UserMenu from './UserMenu';
import SessionWatcher from './SessionWatcher';
import { Spinner } from './AuthShell';
import InvitePage from './InvitePage';
import AccessScreen from './AccessScreen';
import { AccountMenuContext, type AccountMenuValue } from './AccountMenuContext';

type Phase = 'loading' | 'error' | 'locked' | 'open' | 'login' | 'app';

/**
 * Whose patient data lives in this tab's sessionStorage (opaque `uid` from /api/auth/me, never an e-mail).
 * Survives reloads and tab discards, so a DIFFERENT person logging in on the same tab never gets it.
 */
export const TAB_OWNER_KEY = 'wiscv-session-owner';
const readOwner = (): string | null => { try { return sessionStorage.getItem(TAB_OWNER_KEY); } catch { return null; } };
const writeOwner = (uid: string) => { try { sessionStorage.setItem(TAB_OWNER_KEY, uid); } catch { /* ignore */ } };
const PENDING_WIPE = 'pending-wipe';
const wipeTab = () => { try { sessionStorage.clear(); } catch { /* ignore */ } };

/**
 * Last auth mode this browser saw (non-sensitive: only 'off' | 'session'). When /api/auth/status is unreachable
 * (API down, 5xx, offline) the app must not lock people out in mode off — but must not open in mode session.
 */
export const MODE_HINT_KEY = 'wiscv-auth-mode-seen';
const readModeHint = (): string | null => { try { return localStorage.getItem(MODE_HINT_KEY); } catch { return null; } };
const writeModeHint = (m: 'off' | 'session') => { try { if (readModeHint() !== m) localStorage.setItem(MODE_HINT_KEY, m); } catch { /* ignore */ } };
/** Retries for a failing /api/auth/status (ms before each retry). */
export const STATUS_RETRY_DELAYS_MS = [300, 900];
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
const isFailClosed = (e: unknown) => e instanceof ApiError && e.code === 'AUTH_REQUIRED_UNAVAILABLE';

async function statusWithRetry(): Promise<AuthStatus> {
  for (let i = 0; ; i++) {
    try { return await getStatus(); }
    catch (e) {
      if (isFailClosed(e) || i >= STATUS_RETRY_DELAYS_MS.length) throw e;
      await sleep(STATUS_RETRY_DELAYS_MS[i]);
    }
  }
}

/**
 * Wraps the whole app at the entry point (App.tsx), NOT inside SimpleWISCVSystem.
 *  - AUTH_MODE=off   → fully transparent (children only);
 *  - AUTH_MODE=session → login screen until a session exists; a 401 from any /api call
 *    shows the login as an OVERLAY while the app stays mounted (patient state is untouched).
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [expired, setExpired] = useState(false);
  const [access, setAccess] = useState(false);
  const [notice, setNotice] = useState<LoginNotice>(() =>
    new URLSearchParams(window.location.search).get('login') === 'denied' ? 'denied' : null);
  const isInvite = window.location.pathname === '/zaproszenie';

  // The account that was signed in before the session expired (in memory; sessionStorage covers reloads).
  const ownerRef = useRef<string | null>(null);
  // true while the app (children) is mounted — then a different user needs a hard reload, not just a re-render.
  const appMountedRef = useRef(false);
  useEffect(() => { appMountedRef.current = phase === 'app'; }, [phase]);

  const refresh = useCallback(async () => {
    try {
      let s: AuthStatus;
      try { s = await statusWithRetry(); }
      catch (e) {
        // The server says auth is required but its auth store is gone: never open, explain why.
        if (isFailClosed(e)) { setPhase('locked'); return; }
        // Unreachable: in mode off (or never seen) the app works as before; after seeing session → error screen.
        if (readModeHint() !== 'session') { setSharedDiagnoses(); setPhase('open'); return; }
        setPhase('error'); return;
      }
      writeModeHint(s.mode);
      setStatus(s);
      if (s.mode === 'off') { setSharedDiagnoses(); setPhase('open'); return; }
      let m: Me;
      try { m = await getMe(); } catch { lockDiagnoses(); setPhase('login'); return; }
      const id = m.uid || `email:${m.email}`; // uid is always sent by this server; the fallback only guards old mocks/servers
      const prev = ownerRef.current ?? readOwner();
      if (prev && prev !== id) {
        // Somebody else signed in (expiry overlay, or a reloaded tab): the previous person's patient data
        // must not survive — wipe the tab and remount the whole app from scratch.
        wipeTab();
        if (appMountedRef.current) {
          // Unmount the app first (stops its debounced sessionStorage writers and the beforeunload prompt), and mark
          // the tab "wipe pending" instead of B: anything A's code still writes before the unload is wiped again on
          // the fresh load, because the owner then differs from B.
          writeOwner(PENDING_WIPE); ownerRef.current = PENDING_WIPE;
          setMe(null); setPhase('loading');
          nav.reloadToRoot();
          return;
        }
      }
      writeOwner(id); ownerRef.current = id;
      // Saved diagnoses (localStorage) are per account; set BEFORE the app mounts (it reads them on mount).
      if (m.uid) setDiagnosesOwner(m.uid, m.role); else lockDiagnoses();
      setMe(m); setExpired(false); setNotice(null); setPhase('app');
    } catch { setPhase('error'); }
  }, []);

  useEffect(() => { if (!isInvite) refresh(); }, [isInvite, refresh]);
  useEffect(() => onUnauthorized(() => setExpired(true)), []);
  const markExpired = useCallback(() => setExpired(true), []);
  // How many app headers render the account menu inline (AccountMenuSlot); 0 → floating fallback.
  const [menuHosts, setMenuHosts] = useState(0);
  const registerMenuHost = useCallback(() => {
    setMenuHosts(n => n + 1);
    return () => setMenuHosts(n => n - 1);
  }, []);
  useEffect(() => {
    if (notice === 'denied') window.history.replaceState(null, '', window.location.pathname);
  }, [notice]);

  if (isInvite) return <InvitePage />;
  if (phase === 'loading') return <div role="status" className="auth-page app-canvas min-h-screen flex items-center justify-center gap-3 text-txt-secondary"><Spinner />Ładowanie…</div>;
  if (phase === 'locked') {
    return (
      <div role="alert" className="app-canvas min-h-screen flex flex-col items-center justify-center gap-3 p-4 text-center text-txt-primary">
        <p>Logowanie jest wymagane, ale serwer logowania jest niedostępny.</p>
        <p className="text-sm text-txt-secondary">Aplikacja pozostaje zamknięta. Skontaktuj się z administratorem.</p>
        <button type="button" onClick={refresh} className="min-h-[44px] rounded-lg border border-brd bg-surface-card px-4 hover:bg-surface-subtle">Spróbuj ponownie</button>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div className="app-canvas min-h-screen flex flex-col items-center justify-center gap-3 p-4 text-txt-primary">
        <p>Nie udało się połączyć z serwerem.</p>
        <button type="button" onClick={refresh} className="min-h-[44px] rounded-lg border border-brd bg-surface-card px-4 hover:bg-surface-subtle">Spróbuj ponownie</button>
      </div>
    );
  }
  if (phase === 'open') return <>{children}</>;
  if (phase === 'login' && status) return <LoginScreen status={status} notice={notice} onLoggedIn={refresh} />;

  // phase === 'app'
  // Explicit sign-out wipes the tab's patient data (sessionStorage) — expiry, in contrast, keeps it.
  const leave = async (all: boolean) => {
    try { await (all ? logoutAll() : logout()); } catch { /* cookie is cleared server-side; fall through to the login screen */ }
    try { sessionStorage.removeItem('wiscv-legacy-state-v1'); sessionStorage.removeItem('wiscv-partial-stream'); sessionStorage.removeItem(TAB_OWNER_KEY); } catch { /* ignore */ }
    ownerRef.current = null;
    lockDiagnoses(); // saved diagnoses stay on the device but are not shown to whoever signs in next
    setMe(null); setExpired(false); setAccess(false); setNotice('loggedOut'); setPhase('login');
  };
  const account: AccountMenuValue | null = me
    ? { me, onAccess: () => setAccess(true), onLogout: () => leave(false), onLogoutAll: () => leave(true), register: registerMenuHost }
    : null;
  return (
    <AccountMenuContext.Provider value={account}>
      {children}
      {expired && status && <LoginScreen status={status} overlay onLoggedIn={refresh} />}
      {!expired && <SessionWatcher onExpired={markExpired} />}
      {/* The legacy header renders the menu inline (AccountMenuSlot); other screens (/new) get the fixed top-right fallback. */}
      {account && menuHosts === 0 && <UserMenu me={account.me} onAccess={account.onAccess} onLogout={account.onLogout} onLogoutAll={account.onLogoutAll} />}
      {access && me && <AccessScreen selfEmail={me.email} onClose={() => setAccess(false)} />}
    </AccountMenuContext.Provider>
  );
};

export default AuthGate;
